<script lang="ts" setup>
import type { Country } from '@services/countries';
import { computed, onMounted, reactive, ref } from 'vue';
import type { SelectOption } from './types';

const properties = defineProps<{
	nameLabel: string;
	emailLabel: string;
  prefixLabel: string;
  phoneLabel: string;
  selectLabel: string;
  subcategorySelectLabel: string;
  selectOptions: SelectOption[],
  checkLabel: string;
  buttonText: string;
  commentLabel: string;
  countries: Country[],
  successMessage: string;
  errorMessage: string;
  urlForm: string;
}>();

type SelectOptionType = {
  content: string,
  value: string,
  subCategories?: SelectOption[],
  selected?: boolean,
};

const form = reactive<{
  fullName: string;
  email: string;
  phone_prefix: string;
  phone: string;
  category: string;
  subcategory?: string;
  commercialInfo: boolean;
  message: string;
}>({
	fullName: "",
  email: "",
  phone_prefix: "+34",
  phone: "",
  category: "",
  subcategory: undefined,
  commercialInfo: false,
  message: ""
});

const optionsSelected = ref<SelectOptionType[]>([]);
const subCategorySelected = ref<SelectOptionType[]>([]);
const emailSended = ref<boolean>(false);
const errorSending = ref<boolean>(false);

const selectSubcategories = ref<SelectOption[]>([]);

onMounted(() => {
  const queryString = window?.location?.search;
  if (queryString?.length > 0) {
    const urlParams = new URLSearchParams(queryString);
    const subcategoryParam = urlParams.has("sub") ? String(urlParams.get("sub")).toLowerCase() : undefined;
    if (!subcategoryParam) {
      return;
    }

    const category = getOptions.value.find((c) => c.subCategories?.find((sub) => sub.category.toLowerCase().includes(subcategoryParam)));
    if (!category) {
      return;
    }

    updateOptionsSelected([{
      ...category,
      selected: true,
    }]);

    const subcategory = getSubcategoryOptions.value.find((c) => c.value.toLowerCase().includes(subcategoryParam))
    if (!subcategory) {
      return;
    }

    updateSubcategorySelected([{
      ...subcategory,
      selected: true,
    }]);
  }
})

const getOptions = computed(() => properties.selectOptions.map((op) => ({
  content: op.category,
  value: op.category,
  subCategories: op.subCategories,
})))

const getSubcategoryOptions = computed(() => selectSubcategories.value.map((op) => ({
  content: op.category,
  value: op.category,
  subCategories: op.subCategories,
})))

const updatePhone = (value: string) => {
	form.phone = value;
};

const updatePrefix = (value: string) => {
	form.phone_prefix = value;
};

const updateOptionsSelected = (options: SelectOptionType[]) => {
	optionsSelected.value = options;

	if (options.length > 0) {
    form.category = options?.[0]?.value;
  } else {
    form.category = "";
  }

  selectSubcategories.value = options?.[0]?.subCategories ?? [];
  form.subcategory = undefined;
  subCategorySelected.value = [];
};

const updateSubcategorySelected = (options: SelectOptionType[]) => {
	subCategorySelected.value = options;

	if (options.length > 0) {
    form.subcategory = options?.[0]?.value;
  } else {
    form.subcategory = undefined;
  }
};

const toggleCheck = () => {
	form.commercialInfo = !form.commercialInfo;
};

const saveForm = async () => {
  emailSended.value = false;
  errorSending.value = false;

  let formData = new FormData();
  formData.append('NOMBRE', form.fullName);
  formData.append('EMAIL', form.email);
  formData.append('PHONE__COUNTRY_CODE', form.phone_prefix);
  formData.append('PHONE', form.phone);
  formData.append('COMMENTS', form.message);
  formData.append('OPT_IN', form.commercialInfo ? "1" : "0");
  formData.append('ADDITIONAL_INFO', `${form.category ?? ""} ${form.subcategory ?? ""}`.trim())
  formData.append('email_address_check', "");
  formData.append('locale', "es");

  try {
    const response = await fetch(properties.urlForm, {
    method: "POST",
    body: formData,
  });

    if (response.ok || response.status) {
      console.log("Form sended", {
        ...form
      });
      emailSended.value = true;
      form.fullName = "";
      form.email = "";
      form.phone_prefix = "";
      form.phone = "";
      form.category = "";
      form.subcategory = undefined;
      form.commercialInfo = false;
      form.message = "";
    } else {
      console.error("Error to send the form");
      errorSending.value = true;
    }
  } catch (error) {
    /* It is not the best but it is to avoid the CORS problem */
    emailSended.value = true;
    console.error(error);
  }
}
</script>

<template>
  <div class="form-part">
    <template v-if="!emailSended">
      <form class="form-part__form" id="contact-form" @submit.prevent="saveForm">
        <ods-input maxlength="50" variant="orbyn" type="text" v-model="form.fullName" :label="properties.nameLabel" id="full-name" :required="true" />
        <ods-input maxlength="50" variant="orbyn" type="email" v-model="form.email" :label="properties.emailLabel" id="email" :required="true" />
        <ods-phone
          :key="`phone-input-${properties.countries.length}`"
          :model-value-country="form.phone_prefix"
          :model-value-phone="form.phone"
          :options="properties.countries"
          variant="orbyn"
          maxlength="20"
          id="phone"
          :prefix-place-holder="properties.prefixLabel"
          :label="properties.phoneLabel"
          :required="true"
          @update:model-value-phone="updatePhone"
          @update:model-value-country="updatePrefix"
        />
        <ods-select
          :model-value="optionsSelected"
          :elements="getOptions"
          :multiple="false"
          variant="orbyn"
          id="category"
          :label="properties.selectLabel"
          :required="true"
          @update:model-value="updateOptionsSelected"
        />
        <ods-select
          v-if="getSubcategoryOptions.length > 0"
          :model-value="subCategorySelected"
          :elements="getSubcategoryOptions"
          :multiple="false"
          variant="orbyn"
          id="subcategory"
          :label="properties.subcategorySelectLabel"
          :required="true"
          @update:model-value="updateSubcategorySelected"
        />
        <textarea class="form-part__form__textarea" id="comment" v-model="form.message" name="comments" :placeholder="commentLabel" rows="4" cols="50"></textarea>
        <div class="form-part__form__check-part">
          <ods-checkbox
            id="accept-check"
            :key="`check-${form.commercialInfo}`"
            :checked="form.commercialInfo"
            value="accept"
            variant="orbyn"
            @action:click="toggleCheck"
          />
          <label for="accept-check" class="form-part__form__check-part__label">
            {{ properties.checkLabel }}
          </label>
        </div>
      </form>
      <p v-if="errorSending" class="form-part__error">{{  properties.errorMessage }}</p>
      <ods-button
        class="form-part__button"
        type="submit"
        variant="orbyn"
        size="large"
        button-type="filled"
        form="contact-form"
      >
        {{ properties.buttonText }}
      </ods-button>
    </template>
    <div v-if="emailSended" class="form-part__success"><p>{{  properties.successMessage }}</p></div>
  </div>
</template>

<style scoped>

.form-part {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;

  .form-part__form {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .form-part__form__textarea {
      border: 0.5px solid #a9a9bc;
      background-color: #ffffff;
      color: #2d2d39;
      border-radius: 0.5rem;
      padding: 0.5rem 1rem;

      &:hover {
        border-color: #6666ff;
      }

      &:focus-within {
        border-color: #0000ff;
      }

      &::-webkit-input-placeholder {
        color: #5a5a72;
      }

      &:-moz-placeholder {
        color: #5a5a72;
        opacity:  1;
      }

      &::-moz-placeholder {
        color: #5a5a72;
        opacity:  1;
      }

      &:-ms-input-placeholder {
        color: #5a5a72;
      }

      &::-ms-input-placeholder {
        color: #5a5a72;
      }
      &::placeholder {
        color: #5a5a72;
      }
    }

    .form-part__form__check-part {
			display: flex;
			flex-direction: row;
			align-items: center;
			gap: 0.75rem;

      .form-part__form__check-part__label {
        font-size: 0.875rem;
        font-weight: 400;
        line-height: 1.5rem;
        text-align: left;
        color:#434356;
      }
		}
  }

  .form-part__button {
    width: 100%;
  }

  .form-part__success {
    text-align: left;
    width: 100%;
    color: #21ba40;
  }

  .form-part__error {
    color: #ff1100;
  }
}

@media (min-width: 720px) {
  .form-part__button {
    max-width: 11.625rem;
  }
}
</style>